import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { formatPrice } from "@/utils/currency";
import { useAuth } from "@/contexts/AuthContext";
import { SendReportEmail } from "./SendReportEmail";

export function AccountingReportsSummary({ summary, period, dateFrom, dateTo }) {
  const { authAxios } = useAuth();

  const formatDate = (value) => {
    if (!value) return "No informado";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "No informado";
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  };

  const downloadFile = async (endpoint, filename) => {
    try {
      let url = `/financiero/${endpoint}`;

      if (period === "custom") {
        url += `?date_from=${dateFrom}&date_to=${dateTo}`;
      } else {
        url += `?period=${period}`;
      }

      const response = await authAxios.get(url, { responseType: "blob" });
      const url_blob = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url_blob;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url_blob);
      toast.success(`${filename} descargado`);
    } catch (err) {
      toast.error("Error al descargar archivo");
    }
  };


  if (!summary) return null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-sm uppercase tracking-tight">Información del Informe</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-muted-foreground">Empresa</p>
              <p className="font-semibold">{summary.restaurant.name}</p>
            </div>
            <div>
              <p className="text-muted-foreground">CIF/NIF</p>
              <p className="font-semibold">{summary.restaurant.nif || "N/A"}</p>
            </div>
            <div>
              <p className="text-muted-foreground">Período</p>
              <p className="font-semibold">
                {formatDate(summary.period.start)} - {formatDate(summary.period.end)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-tight text-green-900">Ventas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Facturación bruta:</span>
              <span className="font-semibold">{formatPrice(summary.sales.gross_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Descuentos:</span>
              <span className="font-semibold">-{formatPrice(summary.sales.discount_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Base imponible:</span>
              <span className="font-semibold">{formatPrice(summary.sales.net_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">IVA repercutido (registrado):</span>
              <span className="font-semibold">
                {summary.sales.tax_fiscal_total !== null
                  ? formatPrice(summary.sales.tax_fiscal_total)
                  : "No informado"
                }
              </span>
            </div>
            <div className="text-xs bg-blue-100 text-blue-700 p-2 rounded">
              {summary.sales.count_with_fiscal} con datos fiscales / {summary.sales.count_without_fiscal} sin datos
            </div>
            {summary.sales.fiscal_note && (
              <div className="text-xs italic text-muted-foreground bg-amber-50 p-2 rounded">
                {summary.sales.fiscal_note}
              </div>
            )}
            <hr className="my-2" />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total cobrado:</span>
              <span className="font-semibold text-green-700">{formatPrice(summary.sales.paid_amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total pendiente:</span>
              <span className="font-semibold text-orange-600">{formatPrice(summary.sales.pending_amount)}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {summary.sales.count} transacciones
            </div>
          </CardContent>
        </Card>

        <Card className="bg-red-50 border-red-200">
          <CardHeader>
            <CardTitle className="text-base uppercase tracking-tight text-red-900">Gastos</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Gastos totales (brutos):</span>
              <span className="font-semibold">{formatPrice(summary.expenses.gross_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Base imponible (gastos):</span>
              <span className="font-semibold">{formatPrice(summary.expenses.net_total)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">IVA soportado:</span>
              <span className="font-semibold">
                {summary.expenses.tax_total !== null
                  ? formatPrice(summary.expenses.tax_total)
                  : "No informado"
                }
              </span>
            </div>
            <hr className="my-2" />
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total pagado:</span>
              <span className="font-semibold text-red-700">{formatPrice(summary.expenses.paid_amount)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Total pendiente:</span>
              <span className="font-semibold text-orange-600">{formatPrice(summary.expenses.pending_amount)}</span>
            </div>
            <div className="text-xs text-muted-foreground mt-2">
              {summary.expenses.count} facturas
            </div>
          </CardContent>
        </Card>
      </div>

      <Card className={summary.result >= 0 ? "bg-blue-50 border-blue-200" : "bg-red-50 border-red-200"}>
        <CardHeader>
          <CardTitle className="text-base uppercase tracking-tight">Resultado del Período</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold">Beneficio/Pérdida:</span>
            <span className={`text-3xl font-bold ${summary.result >= 0 ? "text-green-600" : "text-red-600"}`}>
              {formatPrice(summary.result)}
            </span>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3">
        <Button
          onClick={() => downloadFile("accounting-reports/excel", "informe_contable.xlsx")}
          className="w-full gap-2 bg-blue-600 hover:bg-blue-700"
        >
          <Download className="w-4 h-4" />
          Descargar Excel
        </Button>
        <Button
          onClick={() => downloadFile("accounting-reports/csv", "informe_contable.csv")}
          variant="outline"
          className="w-full gap-2"
        >
          <Download className="w-4 h-4" />
          Descargar CSV
        </Button>
      </div>

      <SendReportEmail summary={summary} period={period} dateFrom={dateFrom} dateTo={dateTo} />
    </>
  );
}
